import { PriceHistory, Resolution } from '@api/api/price-schema';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQueries } from '@tanstack/react-query';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import {
  fetchPrices,
  getEntityPriceVenue,
} from '@terminal/services/price/price-service';

const QUERY_KEY_PRICE_CHANGE_HISTORY = 'QUERY_KEY_PRICE_CHANGE_HISTORY';

interface UsePriceHistoryListProps {
  tags: string[];
  tickers: string[];
  dateFrom: string;
  dateTo: string;
  resolution: Resolution;
  enabled?: boolean;
}

export interface PriceHistoryListItem {
  data: PriceHistory;
  entityTag: string;
}

export interface UsePriceHistoryListResult {
  data: PriceHistoryListItem[];
  isLoading: boolean;
  isError: boolean;
}

export const usePriceHistoryList = ({
  tags,
  tickers,
  dateFrom,
  dateTo,
  resolution,
  enabled = true,
}: UsePriceHistoryListProps): UsePriceHistoryListResult => {
  const { data: entities, isFetched: areEntitiesFetched } = useEntities({
    entityTags: tags,
    enabled,
  });
  const venueByTag = new Map(
    entities.map(entity => [entity.tag, getEntityPriceVenue(entity)])
  );

  const isWaitingForVenues = !areEntitiesFetched;

  return useQueries({
    queries: tags.map((entityTag, index) => {
      const venue = venueByTag.get(entityTag);

      // `areEntitiesFetched` only means every entity query settled — it's true
      // even when one errored or resolved without data, which drops it from
      // `venueByTag`. Gate each request on its own venue so a settled-but-
      // unresolved entity can't fire a US-defaulted v2 request for what may be
      // a non-US instrument.
      const hasResolvedVenue = venueByTag.has(entityTag);

      return {
        queryKey: [
          QUERY_KEY_PRICE_CHANGE_HISTORY,
          entityTag,
          tickers[index],
          resolution,
          dateFrom,
          dateTo,
          venue?.exchange,
          venue?.country,
        ],
        queryFn: () =>
          fetchPrices({
            entityTag,
            ticker: tickers[index],
            resolution,
            dateFrom,
            dateTo,
            exchange: venue?.exchange,
            country: venue?.country,
          }),
        staleTime: getTimeTillEndOfDayMs(),
        enabled: enabled && !isWaitingForVenues && hasResolvedVenue,
      };
    }),
    combine: (results): UsePriceHistoryListResult => ({
      data: results.flatMap((r, index) =>
        r.data ? [{ data: r.data, entityTag: tags[index] }] : []
      ),
      isLoading:
        (enabled && isWaitingForVenues) || results.some(r => r.isLoading),
      isError: results.every(r => r.isError),
    }),
  });
};
